import React, { FC, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useLocation, useNavigate } from 'react-router-dom';
import Divider from 'components/divider/Divider';

import Lottie from 'lottie-react';
import {
  CaretRightIcon,
  GreyStrokeInfoIcon,
  NavCloseIcon,
  OrangeInfoIcon,
  RedirectIcon,
  UpcomingPayoutsBankIcon,
  WhiteRightArrowSmall
} from 'assets/icons';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectCurrentTeam,
  selectCurrentTeamInvoicePermissions,
  selectIsOwner,
  selectMerchantCustomizations,
  selectPaymentSystemConfig,
  selectTeamBankDetails,
  showNotifier
} from 'containers/app/appSlice';
import { useLazyGetPaymentSystemConfigQuery, useLazyGetTeamBankAccountQuery } from 'containers/app/api';
import { useLazyGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { WalletBankAccountReconnectModal } from 'containers/legal-reacceptance';
import { useLazyGetUpcomingPayoutsSummaryQuery } from 'containers/payouts/api';
import { LoadingSpinnerAnimation } from 'assets/animations';
import {
  BANK_NOT_LINKED_PAUSE_DESC,
  CHANGE_BANK_ACTION_REQUIRED_PAUSE_DESC,
  CHANGE_BANK_ACTION_REQUIRED_PAUSE_DESC_NON_OWNER,
  CHANGE_BANK_IN_REVIEW_PAUSE_DESC,
  COMPLETE_BANK_VERIFICATION_CTA,
  GENERIC_PAUSE_CONTACT_OWNER,
  GENERIC_PAUSE_CONTACT_SUPPORT,
  GENERIC_PAUSE_DESC,
  KYC_COMPLETE_VERIFICATION_CTA,
  PaymentSystem,
  PayoutRootPage,
  PayoutScheduleType,
  RISK_HOLD_DESC,
  RISK_REVIEW_IN_PROGRESS_DESC,
  RISK_REVIEW_IN_PROGRESS_TITLE,
  RISK_SUBMIT_ADDITIONAL_DATA_CTA,
  VERIFICATION_ACTION_NEEDED_DESC,
  VERIFICATION_ACTION_NEEDED_TITLE,
  WA_RECONNECT_PAUSE_CTA,
  WA_RECONNECT_PAUSE_DESC
} from 'containers/payouts/constants';
import { CustomButton, If, NoPermissions, SidePanel } from 'components';
import { NotifierTypes } from 'constants/notifierConstants';
import { checkPermissions } from 'containers/payouts/utils';
import { PayoutsCleverTapEvents } from 'containers/payouts/events';
import { logAnalyticEvent } from 'utils/analytics';
import { openScanpayFAQInNewTab } from 'utils/urlUtils';
import routesPath from 'routes/RoutesPath';
import { isCAorWA } from 'utils/commonUtils';
import { WalletBankViewSource } from 'containers/legal-reacceptance/legalReacceptance.model';
import { getWalletBankScreenSeenCount } from 'containers/legal-reacceptance/utils';
import { getPayoutScheduleViewState } from 'containers/payouts/payouts-settings/getPayoutScheduleViewState';
import DateUtils from 'utils/dateUtils';

const formatOnboardingDataLastSubmittedOn = (onboardingDataLastSubmittedOn: string | null | undefined): string => {
  if (!onboardingDataLastSubmittedOn) return '';
  return DateUtils.getDateInFormat({ date: new Date(onboardingDataLastSubmittedOn), dateFormat: 'MMM dd, yyyy' });
};

const UpComingPayoutDetailsPanel: FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { handleSidePanelClose } = useOutletContext<any>();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isOwner = useAppSelector(selectIsOwner);
  const canTakeAction = isOwner;
  const currentTeamBank = useAppSelector(selectTeamBankDetails);
  const navigate = useNavigate();
  const [getUpcomingPayoutsSummaryQuery, getUpcomingPayoutsSummaryState] = useLazyGetUpcomingPayoutsSummaryQuery();
  const [getMerchantCustomizationsQuery] = useLazyGetMerchantCustomizationsQuery();
  const [getPaymentSystemConfigQuery] = useLazyGetPaymentSystemConfigQuery();
  const [getTeamBankAccountQuery] = useLazyGetTeamBankAccountQuery();
  const {
    data: upcomingPayoutsSummaryData,
    isSuccess: upcomingPayoutsSummarySuccess,
    isFetching: fetchingUpcomingPayoutsSummary
  } = getUpcomingPayoutsSummaryState;

  const { inFutureAmount, keyLabelMapping, payoutSendingVia } = upcomingPayoutsSummaryData ?? {};
  const {
    payoutSchedule,
    bankingEnabled,
    paymentSystem,
    payoutType,
    hasInReviewTeamBankAccount,
    hasActionRequiredTeamBankAccount
  } = useAppSelector(selectPaymentSystemConfig);
  const payoutPermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const merchantCustomizations = useAppSelector(selectMerchantCustomizations);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const [isWalletBankReconnectModalOpen, setIsWalletBankReconnectModalOpen] = useState(false);
  const [walletBankScreenSeenCount, setWalletBankScreenSeenCount] = useState(0);
  const walletBankReconnectViewSource: WalletBankViewSource = 'upcoming_payout_details';
  const { bankName, accountNumber } = currentTeamBank;

  const hasWalletEligibleBankAccount = merchantCustomizations?.hasWalletEligibleBankAccount ?? false;
  const isLegalAgreementReAcceptanceRequired =
    merchantCustomizations?.pendingActions?.legalAgreementReAcceptanceRequired ?? false;

  const payoutScheduleView = getPayoutScheduleViewState({
    bankingEnabled,
    payoutSchedule,
    payoutType,
    hasActionRequiredTeamBankAccount,
    hasInReviewTeamBankAccount,
    isOwner,
    paymentSystem,
    merchantCustomizationsLoaded: merchantCustomizations != null,
    hasWalletEligibleBankAccount,
    isLegalAgreementReAcceptanceRequired,
    connectBankingStatus: currentTeam?.connectBankingStatus,
    internalBankingStatus: currentTeam?.internalBankingStatus,
    connectApplicationStatus: currentTeam?.connectApplicationStatus,
    isAdditionalDataRequiredForRisk: currentTeam?.isAdditionalDataRequiredForRisk ?? false,
    isRiskReviewDone: currentTeam?.isRiskReviewDone ?? true,
    isHoldAndReleaseEnabled: currentTeam?.isHoldAndReleaseEnabled ?? false,
    onboardingDataLastSubmittedOn: currentTeam?.onboardingDataLastSubmittedOn
  });

  useEffect(() => {
    if (currentTeam.id && paymentSystem) {
      setHasPermissionsToView(
        checkPermissions({
          paymentSystem,
          viewPayoutsBySelf: payoutPermissions.viewPayoutsBySelf,
          viewPayoutsByTeam: payoutPermissions.viewPayoutsByTeam
        })
      );
    }
  }, [currentTeam.id, paymentSystem]);

  useEffect(() => {
    if (currentTeam.id && hasPermissionsToView) getUpcomingPayoutsSummaryQuery(currentTeam.id);
  }, [currentTeam.id, hasPermissionsToView]);

  useEffect(() => {
    setHasPermissionsToView(
      checkPermissions({
        paymentSystem,
        viewPayoutsBySelf: payoutPermissions.viewPayoutsBySelf,
        viewPayoutsByTeam: payoutPermissions.viewPayoutsByTeam
      })
    );
  }, [payoutPermissions.viewPayoutsBySelf, payoutPermissions.viewPayoutsByTeam]);

  useEffect(() => {
    let isCancelled = false;
    if (!currentTeam.id) {
      setWalletBankScreenSeenCount(0);
      return () => {
        isCancelled = true;
      };
    }
    const loadWalletBankScreenSeenCount = async () => {
      const rawCount = await getWalletBankScreenSeenCount(currentTeam.id);
      if (isCancelled) return;
      setWalletBankScreenSeenCount(rawCount);
    };
    void loadWalletBankScreenSeenCount();
    return () => {
      isCancelled = true;
    };
  }, [currentTeam.id, isWalletBankReconnectModalOpen]);

  const viewAllTransactions = () => {
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Collected & Refunds filter applied',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    logAnalyticEvent(PayoutsCleverTapEvents.webUpcomingPayoutsViewAllTransactionsClick);
    navigate(`/payouts/balance/${currentTeam.id}/upcoming-payouts/${PayoutRootPage.BALANCE}/transactions`);
  };

  const handleAddBankAccount = () => {
    if (isCAorWA(paymentSystem)) {
      navigate(routesPath.PAYOUTS_SETTINGS);
    } else {
      navigate(routesPath.BANKING);
    }
  };

  const onSeePayoutScheduleClick = () => {
    navigate(routesPath.PAYOUTS_SETTINGS);
  };

  const handleWalletBankReconnectAfterComplete = () => {
    if (!currentTeam.id) return;
    getMerchantCustomizationsQuery({ teamId: currentTeam.id.toString() });
    getPaymentSystemConfigQuery(currentTeam.id);
    getTeamBankAccountQuery(currentTeam.id);
    getUpcomingPayoutsSummaryQuery(currentTeam.id);
  };

  const handleOpenWalletBankReconnectModal = () => {
    setIsWalletBankReconnectModalOpen(true);
  };

  const handleActionRequiredGoToSettings = () => {
    navigate(routesPath.PAYOUTS_SETTINGS);
  };

  const handleCompleteKycVerification = () => {
    navigate(routesPath.PAYOUTS_SETTINGS);
  };

  const handleSubmitAdditionalRiskData = () => {
    navigate(routesPath.HOME);
  };

  const getPausedScheduleBanner = () => {
    switch (payoutScheduleView.kind) {
      case 'WA_RECONNECT_PAUSE':
        return (
          <div
            className={
              'mb-2.5 flex flex-col gap-3 rounded-md border border-pending-orange bg-[#F7E2C0]/20 ' + 'py-4 pl-3 pr-5'
            }>
            <div className="flex gap-3">
              <OrangeInfoIcon className="shrink-0" />
              <div className="text-sbase font-normal text-primaryText">{WA_RECONNECT_PAUSE_DESC}</div>
            </div>
            <CustomButton
              id="upcoming-payouts-wa-paused-add-bank-btn"
              onClick={handleOpenWalletBankReconnectModal}
              className="h-10 w-full shrink-0 text-sbase font-semibold text-white"
              EndIcon={WhiteRightArrowSmall}>
              {WA_RECONNECT_PAUSE_CTA}
            </CustomButton>
          </div>
        );
      case 'ACTION_REQUIRED_PAUSE':
        return (
          <div
            className={
              'mb-2.5 flex flex-col gap-3 rounded-md border border-pending-orange bg-[#F7E2C0]/20 ' + 'py-4 pl-3 pr-5'
            }>
            <div className="flex gap-3">
              <OrangeInfoIcon className="shrink-0" />
              <div className="text-sbase font-normal text-primaryText">
                {isOwner ? CHANGE_BANK_ACTION_REQUIRED_PAUSE_DESC : CHANGE_BANK_ACTION_REQUIRED_PAUSE_DESC_NON_OWNER}
              </div>
            </div>
            {isOwner && (
              <CustomButton
                id="upcoming-payouts-action-required-settings-btn"
                onClick={handleActionRequiredGoToSettings}
                className="h-10 w-full shrink-0 text-sbase font-semibold text-white"
                EndIcon={WhiteRightArrowSmall}
                aria-label="Go to payout settings to complete bank verification">
                {COMPLETE_BANK_VERIFICATION_CTA}
              </CustomButton>
            )}
          </div>
        );
      case 'IN_REVIEW_PAUSE':
        return (
          <div
            className={
              'mb-2.5 flex flex-col gap-3 rounded-md border border-pending-orange bg-[#F7E2C0]/20 ' + 'py-4 pl-3 pr-5'
            }>
            <div className="flex gap-3">
              <OrangeInfoIcon className="shrink-0" />
              <div className="text-sbase font-normal text-primaryText">{CHANGE_BANK_IN_REVIEW_PAUSE_DESC}</div>
            </div>
          </div>
        );
      case 'BANK_NOT_LINKED':
        return (
          <div className="flex flex-col items-center gap-3 rounded-md border border-pending-orange p-4 pl-3">
            <div className="flex gap-3">
              <UpcomingPayoutsBankIcon className="shrink-0" />
              <div className="text-sbase font-normal text-primaryText">{BANK_NOT_LINKED_PAUSE_DESC}</div>
            </div>
            <CustomButton
              id="upcoming-payouts-panel-add-bank-btn"
              onClick={handleAddBankAccount}
              className="h-7 text-sbase font-semibold text-white"
              EndIcon={WhiteRightArrowSmall}>
              Add a bank account
            </CustomButton>
          </div>
        );
      case 'KYC_ACTION_REQUIRED':
        return (
          <div
            className={
              'mb-2.5 flex flex-col gap-3 rounded-md border border-pending-orange bg-[#F7E2C0]/20 ' + 'py-4 pl-3 pr-5'
            }>
            <div className="flex gap-3">
              <OrangeInfoIcon className="shrink-0" />
              <div className="flex flex-col gap-1">
                <div className="text-sbase font-semibold text-primaryText">{VERIFICATION_ACTION_NEEDED_TITLE}</div>
                <div className="text-sbase font-normal text-primaryText">{VERIFICATION_ACTION_NEEDED_DESC}</div>
              </div>
            </div>
            {canTakeAction ? (
              <CustomButton
                id="upcoming-payouts-kyc-complete-verification-btn"
                onClick={handleCompleteKycVerification}
                className="h-10 w-full shrink-0 text-sbase font-semibold text-white"
                EndIcon={WhiteRightArrowSmall}
                aria-label="Go to application status to complete verification">
                {KYC_COMPLETE_VERIFICATION_CTA}
              </CustomButton>
            ) : null}
          </div>
        );
      case 'RISK_ADDITIONAL_DATA_REQUIRED':
        return (
          <div
            className={
              'mb-2.5 flex flex-col gap-3 rounded-md border border-pending-orange bg-[#F7E2C0]/20 ' + 'py-4 pl-3 pr-5'
            }>
            <div className="flex gap-3">
              <OrangeInfoIcon className="shrink-0" />
              <div className="flex flex-col gap-1">
                <div className="text-sbase font-semibold text-primaryText">{VERIFICATION_ACTION_NEEDED_TITLE}</div>
                <div className="text-sbase font-normal text-primaryText">{VERIFICATION_ACTION_NEEDED_DESC}</div>
              </div>
            </div>
            {canTakeAction ? (
              <CustomButton
                id="upcoming-payouts-risk-submit-additional-data-btn"
                onClick={handleSubmitAdditionalRiskData}
                className="h-10 w-full shrink-0 text-sbase font-semibold text-white"
                EndIcon={WhiteRightArrowSmall}
                aria-label="Go to home to submit additional data">
                {RISK_SUBMIT_ADDITIONAL_DATA_CTA}
              </CustomButton>
            ) : null}
          </div>
        );
      case 'RISK_REVIEW_IN_PROGRESS':
        return (
          <div
            className={
              'mb-2.5 flex flex-col gap-3 rounded-md border border-pending-orange bg-[#F7E2C0]/20 ' + 'py-4 pl-3 pr-5'
            }>
            <div className="flex gap-3">
              <OrangeInfoIcon className="shrink-0" />
              <div className="flex flex-col gap-1">
                <div className="text-sbase font-semibold text-primaryText">{RISK_REVIEW_IN_PROGRESS_TITLE}</div>
                {formatOnboardingDataLastSubmittedOn(payoutScheduleView.onboardingDataLastSubmittedOn) ? (
                  <div className="text-sbase font-normal text-accentText">
                    Submitted on {formatOnboardingDataLastSubmittedOn(payoutScheduleView.onboardingDataLastSubmittedOn)}
                  </div>
                ) : null}
                <div className="text-sbase font-normal text-primaryText">{RISK_REVIEW_IN_PROGRESS_DESC}</div>
              </div>
            </div>
          </div>
        );
      case 'RISK_HOLD':
        return (
          <div
            className={
              'mb-2.5 flex flex-col gap-3 rounded-md border border-pending-orange bg-[#F7E2C0]/20 ' + 'py-4 pl-3 pr-5'
            }>
            <div className="flex gap-3">
              <OrangeInfoIcon className="shrink-0" />
              <div className="text-sbase font-normal text-primaryText">{RISK_HOLD_DESC}</div>
            </div>
          </div>
        );
      case 'GENERIC_PAUSE':
        return (
          <div
            className={
              'mb-2.5 flex flex-col gap-3 rounded-md border border-pending-orange bg-[#F7E2C0]/20 ' + 'py-4 pl-3 pr-5'
            }>
            <div className="flex gap-3">
              <OrangeInfoIcon className="shrink-0" />
              <div className="text-sbase font-normal text-primaryText">
                {GENERIC_PAUSE_DESC}{' '}
                {payoutScheduleView.contactOwner ? GENERIC_PAUSE_CONTACT_OWNER : GENERIC_PAUSE_CONTACT_SUPPORT}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getActiveScheduleInfo = () => {
    if (paymentSystem === PaymentSystem.LIP) {
      return '';
    }
    if (payoutSchedule === PayoutScheduleType.DAILY) {
      return (
        <>
          <GreyStrokeInfoIcon className="shrink-0 pr-2" height={24} width={24} />
          <div className="flex flex-col gap-2">
            <div className="text-sbase font-normal text-primaryText">
              Automatic payouts are sent every business day for transactions up to 7PM ET.
            </div>
            <div
              className="flex cursor-pointer items-center gap-1 text-sbase font-normal text-secondary"
              role="button"
              tabIndex={0}
              aria-label="See payout schedule"
              onClick={onSeePayoutScheduleClick}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSeePayoutScheduleClick();
                }
              }}>
              {' '}
              See payout schedule{' '}
              <span>
                <CaretRightIcon className="shrink-0" />
              </span>
            </div>
          </div>
        </>
      );
    }
    return '';
  };

  const getPayoutSchedule = () => {
    const pausedBanner = getPausedScheduleBanner();
    if (pausedBanner) {
      return pausedBanner;
    }
    return getActiveScheduleInfo();
  };

  const renderTotalField = () => {
    const { label, value } = keyLabelMapping?.find(item => item.isFinal) ?? {};
    if (!label || !value) return null;
    return (
      <div className="flex items-center justify-between">
        <div className="text-17px font-bold text-primaryText">{label}</div>
        <div className="text-17px font-bold text-tertiaryText">{getAmountWithCurrency(+value)}</div>
      </div>
    );
  };
  const onLearnMoreClick = () => {
    openScanpayFAQInNewTab(PayoutsCleverTapEvents.webUpcomingPayoutsDetailsLearnMoreClick);
  };

  return (
    <>
      <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
        <div className="flex h-full w-96 flex-col">
          <If condition={fetchingUpcomingPayoutsSummary && hasPermissionsToView}>
            <div className="flex h-full w-96 items-center justify-center">
              <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
            </div>
          </If>
          <If condition={(upcomingPayoutsSummarySuccess && !fetchingUpcomingPayoutsSummary) || !hasPermissionsToView}>
            <div className="flex h-full w-full flex-col gap-5 pt-5">
              <div className="flex  w-full items-center gap-2 pl-[14px]">
                <div onClick={handleSidePanelClose} className="cursor-pointer">
                  <NavCloseIcon id="upcoming-payouts-details-panel-close" />
                </div>
                <div className="text-17px font-semibold text-heading">Upcoming payouts</div>
              </div>
              {hasPermissionsToView ? (
                <div className="customNormalScroll h-full overflow-auto">
                  <div
                    className="flex cursor-pointer items-center justify-center bg-secondaryBtn py-2"
                    role="presentation"
                    onClick={onLearnMoreClick}>
                    <RedirectIcon className="shrink-0 pr-2" />
                    <div className="text-sbase font-semibold text-primaryBtn">
                      Learn about getting paid through ScanPay
                    </div>
                  </div>
                  <div className="flex flex-col gap-5 py-[30px]">
                    <div className="flex flex-col gap-2.5 text-center">
                      <div className="text-sbase font-normal text-accentText">Estimated to be paid out</div>
                      <div className="text-3xl font-bold text-tertiaryText">
                        {getAmountWithCurrency(+inFutureAmount)}
                      </div>
                      {bankingEnabled && (
                        <div className="text-sbase font-normal text-accentText">
                          To {bankName} {accountNumber}
                        </div>
                      )}
                    </div>
                    {getPayoutSchedule() && <div className="flex px-[30px]">{getPayoutSchedule()}</div>}
                  </div>
                  <Divider className="mb-5 w-full border-borderGray" />
                  <div className="mt-5 flex flex-col gap-5">
                    <div className="flex flex-col px-[30px]">
                      <div className="text-sbase font-normal text-accentText">Amount breakdown</div>
                      <div className="mt-5 flex flex-col gap-[15px]">
                        {keyLabelMapping?.map(amountBreakdownItem => {
                          if (
                            (amountBreakdownItem.doNotShowWhenZero && +amountBreakdownItem.value === 0) ||
                            amountBreakdownItem.isFinal
                          ) {
                            return null;
                          }
                          return (
                            <div key={amountBreakdownItem.key} className="flex items-center justify-between">
                              <div className="text-17px font-semibold text-primaryText">
                                {amountBreakdownItem.label}
                              </div>
                              <div className="text-17px font-semibold text-tertiaryText">
                                {getAmountWithCurrency(+amountBreakdownItem.value)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <Divider className="my-5 w-full border-borderGray" />
                      {renderTotalField()}
                    </div>
                    {bankingEnabled && (
                      <>
                        <Divider className="w-full border-borderGray" />
                        <div className="flex flex-col px-[30px]">
                          <div className="text-sbase font-normal text-accentText">Overview</div>
                          <div className="mt-5 flex flex-col gap-[15px]">
                            <div className="flex items-start justify-between">
                              <div className="text-17px font-normal text-primaryText">Sending to</div>
                              <div className="text-right text-17px font-normal text-tertiaryText">
                                {bankName && accountNumber ? (
                                  <>
                                    <div>{bankName}</div>
                                    <div>{accountNumber}</div>
                                  </>
                                ) : (
                                  <div>-</div>
                                )}
                              </div>
                            </div>
                            {payoutSendingVia && (
                              <div className="flex items-center justify-between">
                                <div className="text-17px font-normal text-primaryText">Sending via</div>
                                <div className="text-17px font-normal text-tertiaryText">{payoutSendingVia}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    <Divider className="w-full border-borderGray" />
                    {paymentSystem !== PaymentSystem.LIP && (
                      <div
                        className="mx-[30px] flex h-12 cursor-pointer items-center justify-center rounded-md
                      border border-gray-1 text-17px font-normal text-primaryBtn"
                        role="presentation"
                        onClick={viewAllTransactions}>
                        View all transactions
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <NoPermissions />
              )}
            </div>
          </If>
        </div>
      </SidePanel>
      {currentTeam.id && isOwner ? (
        <WalletBankAccountReconnectModal
          open={isWalletBankReconnectModalOpen}
          onClose={() => setIsWalletBankReconnectModalOpen(false)}
          teamId={currentTeam.id}
          onAfterComplete={handleWalletBankReconnectAfterComplete}
          viewSource={walletBankReconnectViewSource}
          walletBankScreenSeenCount={walletBankScreenSeenCount}
        />
      ) : null}
    </>
  );
};

export default UpComingPayoutDetailsPanel;
